import { fetchAuthSession } from 'aws-amplify/auth';
import { get, post, put } from 'aws-amplify/api';

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: token } : {};
}

export const apiClient = {
  async getSessions(limit = 50) {
    const headers = await authHeaders();
    const response = await get({
      apiName: 'TrackBibleAPI',
      path: '/sessions',
      options: {
        headers,
        queryParams: { limit: limit.toString() },
      },
    }).response;
    return response.body.json();
  },

  async createSession(sessionData: {
    timerDuration: number;
    actualTimeSpent: number;
    bookRead: string;
    chaptersRead: number[];
    completedFullDuration: boolean;
  }) {
    const headers = await authHeaders();
    const response = await post({
      apiName: 'TrackBibleAPI',
      path: '/sessions',
      options: {
        headers,
        body: sessionData as any,
      },
    }).response;
    return response.body.json();
  },

  async getProgress(book?: string, readThrough?: number) {
    const headers = await authHeaders();
    const queryParams: Record<string, string> = {};
    if (book) queryParams.book = book;
    if (readThrough !== undefined) queryParams.readThrough = readThrough.toString();
    const response = await get({
      apiName: 'TrackBibleAPI',
      path: '/progress',
      options: {
        headers,
        queryParams: Object.keys(queryParams).length ? queryParams : undefined,
      },
    }).response;
    return response.body.json();
  },

  async getStreak() {
    const headers = await authHeaders();
    const response = await get({
      apiName: 'TrackBibleAPI',
      path: '/streak',
      options: { headers },
    }).response;
    return response.body.json();
  },

  async getProfile() {
    const headers = await authHeaders();
    const response = await get({
      apiName: 'TrackBibleAPI',
      path: '/user/profile',
      options: { headers },
    }).response;
    return response.body.json() as Promise<unknown> as Promise<import('../../../shared/types').UserProfileResponse>;
  },

  async updatePlan(planData: {
    selectedPlan: string;
    currentBook?: string;
    currentChapter?: number;
    currentReadThrough?: number;
  }) {
    const headers = await authHeaders();
    const response = await put({
      apiName: 'TrackBibleAPI',
      path: '/user/plan',
      options: {
        headers,
        body: planData as any,
      },
    }).response;
    return response.body.json();
  },

  async getAuthToken() {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  },
};

export const useApi = () => {
  const getSessions = async () => {
    try {
      const data = await apiClient.getSessions(200);
      return Array.isArray(data) ? data : (data as any)?.sessions ?? [];
    } catch {
      return [];
    }
  };

  const createSession = async (sessionData: {
    book: string;
    chapters: number[];
    duration: number;
    notes?: string;
  }) => {
    return apiClient.createSession({
      timerDuration: sessionData.duration,
      actualTimeSpent: sessionData.duration,
      bookRead: sessionData.book || 'General',
      chaptersRead: sessionData.chapters ?? [],
      completedFullDuration: true,
    });
  };

  const getProfile = async () => {
    try {
      return await apiClient.getProfile();
    } catch {
      return { currentBook: null, currentChapter: null, currentReadThrough: null, weeklyGoal: null, email: null };
    }
  };

  const getStreak = async () => {
    try {
      return await apiClient.getStreak() as { currentStreak: number; longestStreak: number; lastReadDate: string | null };
    } catch {
      return { currentStreak: 0, longestStreak: 0, lastReadDate: null };
    }
  };

  const getProgress = async (book?: string, readThrough?: number) => {
    try {
      return await apiClient.getProgress(book, readThrough) as { bookProgress: Record<string, number>; totalChaptersCompleted: number; completedChapters: Array<{ book: string; chapter: number; readThrough: number; completedAt: string; sessionId: string }>; readThrough: number };
    } catch {
      return null;
    }
  };

  const startNewReadThrough = async (currentReadThrough: number) => {
    return apiClient.updatePlan({
      selectedPlan: 'custom',
      currentBook: 'Genesis',
      currentChapter: 1,
      currentReadThrough: currentReadThrough + 1,
    });
  };

  return { getSessions, createSession, getProfile, getStreak, getProgress, startNewReadThrough };
};
